import { forwardRef } from "react";
import { Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { useGate } from "@/lib/gate";

/**
 * Button wrapper that enforces a paywall gate.
 *
 * When `locked` is true:
 *   - Button becomes visually dimmed with a small lock badge overlay.
 *   - The button is NOT `disabled` (so it still receives clicks) — clicking
 *     opens the paywall dialog via `useGate().openPaywall(reason)`.
 *   - A tooltip announces the required plan.
 *
 * When `locked` is false, it renders the underlying <Button> unchanged.
 *
 * `reason` maps to a copy block in gate.jsx (ai | novels | words).
 */
const LockedButton = forwardRef(function LockedButton(
    { locked, reason = "ai", tooltip, onClick, className = "", children, ...rest },
    ref
) {
    const { openPaywall } = useGate();

    if (!locked) {
        return (
            <Button ref={ref} onClick={onClick} className={className} {...rest}>
                {children}
            </Button>
        );
    }

    const label = tooltip || (
        reason === "novels" ? "Upgrade to Enchanter for unlimited novels"
            : "Upgrade to Enchanter to use AI features"
    );

    // Strip disabled so clicks still register — we want the paywall dialog.
    const { disabled: _disabled, ...safeRest } = rest;

    return (
        <TooltipProvider delayDuration={200}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        ref={ref}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); openPaywall(reason); }}
                        data-locked="true"
                        aria-label={label}
                        className={`relative opacity-60 hover:opacity-80 cursor-pointer ${className}`}
                        {...safeRest}
                        data-testid={rest["data-testid"] ? `${rest["data-testid"]}-locked` : undefined}
                    >
                        <span className="pointer-events-none flex items-center gap-1.5">
                            {children}
                        </span>
                        <Lock className="w-3 h-3 absolute -top-1 -right-1 bg-background rounded-full p-0.5 border border-border shadow-sm text-primary" />
                    </Button>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">{label}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
});

export default LockedButton;
